import _sortBy from 'lodash.sortby';
import { gt, filterBy, notEmpty, equal, and, or, sort } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isEmpty, isNone } from '@ember/utils';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo, hasMany } from 'ember-data/relationships';
import { inject as service } from '@ember/service';
import { modelApiAction } from 'cio/utils/model-api-actions';
import DS from 'ember-data';
import { camelize } from '@ember/string';
import ArrayProxy from '@ember/array/proxy';
import parseTree from 'cio/utils/campaigns/parse-tree';
import { expandTree, getActionIdsInConsolidatedGroup } from 'cio/utils/campaigns/tree-consolidation';
import isValidConversion from 'cio/utils/is-valid-conversion';
import { removeNodeFromGraph } from 'cio/utils/visual-workflow-builder/branching';
import { queryEncode } from 'cio/helpers/query';
import { FrequencyOption } from 'cio/utils/campaigns/date-triggered';
import ApiService from 'cio/services/api';
import ActionModel, { ActionState, ActionType } from 'cio/models/action';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import TagModel from 'cio/models/tag';
import UserOtherAccountModel from 'cio/models/user-other-account';
import {
  SerializedLegacyAttributeFilter,
  SerializedLegacyAttributeFilters,
  SerializedLegacySegmentNegatableFilter,
} from 'cio/types/conditions/legacy-filter';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import deserializeAndNormalizeFormAttributeFilters from 'cio/utils/campaigns/deserialize-normalize-form-attribute-filters';

import GroupCondition from '../app-models/conditions/group';
import EventAttributeCondition from '../app-models/conditions/event-attribute';
import { unixNowMilliseconds, unixNowSeconds } from 'cio/utils/time';
import Ember from 'ember';
import { quickGraphRank } from 'cio/utils/visual-workflow-builder/layout';
import AccountService from 'cio/services/account';
import { webhookUrl } from 'cio/utils/campaigns/webhook-triggered';
import LogModel from 'cio/models/log';
import { ActivityLogType } from 'cio/utils/activity-log-types';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { shortTypeName } from 'cio/utils/campaign-newsletters';
import {
  ConversionType,
  isConversionEventBased,
  isConversionSegmentBased,
  ModelWithConversion,
} from 'cio/utils/conversions';
import { Filters } from 'cio/utils/filters';
import CampaignNoteModel from './campaign-note';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { Channel } from 'cio/utils/metrics/common';

// Every form trigger is a set of 1) required form condition, 2) optional event data filters condition:
// [formCondition, eventDataFiltersCondition]
export type FormTriggeredCampaignFormTriggerFilterChildren =
  | [FormEventAttributeCondition]
  | [FormEventAttributeCondition, GroupCondition];

export type FormTriggeredCampaignFormTriggerFilter = GroupCondition & {
  children: FormTriggeredCampaignFormTriggerFilterChildren;
};

export type EventTriggeredCampaignAttributeFilters = GroupCondition & { children: EventAttributeCondition[] };
export type FormTriggeredCampaignAttributeFilters = GroupCondition & {
  operator: 'or';
  children: FormTriggeredCampaignFormTriggerFilter[];
};

export const DATE_TRIGGERED_ATTRS: readonly (keyof CampaignModel)[] = [
  'dateTriggeredFrequency',
  'dateTriggeredLeadDays',
  'dateTriggeredAttribute',
  'dateTriggeredStartTime',
  'dateTriggeredZone',
  'dateTriggeredFallbackZone',
] as const;

export const ATTRS: readonly (keyof CampaignModel)[] = [
  'anchors',
  'attributeFilters',
  'event',
  'filters',
  'restartMinInterval',
  'restartMode',
  'type',
  'sendToUnsubscribed',
  'useMessageLimits',
  'useWorkspaceMessageLimitsRetry',
  'messageLimitsRetryWindowSecs',
  ...DATE_TRIGGERED_ATTRS,
  'objectTypeId',
  'objectAttributeTriggers',
  'objectFilters',
  'relationshipAttributeTriggers',
  'relationshipFilters',
  'audience',
  'objectCampaign',
  'relationshipCampaign',
  'webhookId',
  'exitOnTriggerOrFilterNotMatched',
  'exitOnConversionMatched',
] as const;

export enum ObjectTriggerType {
  Created = 0, // created
  Deleted = 1, // deleted
  Updated = 2, // updated
}

export enum ObjectRelationshipTriggerType {
  Created = 0, // created
  Deleted = 1, // deleted
  Updated = 2, // updated
}

export enum CampaignType {
  Transactional = 'transactional',
  Behavioral = 'behavioral',
  APITriggered = 'triggered_broadcast',
  Date = 'date',
  Form = 'form',
  Webhook = 'webhook',
  Object = 'object',
  ObjectRelationship = 'relationship',
  None = 'none',
}

export enum CampaignTypeCategory {
  People = 'people',
  Data = 'data',
}

export interface ObjectCampaignTrigger {
  type: ObjectTriggerType;
  objectTypeId: number;
}

export interface RelationshipCampaignTrigger {
  type: ObjectRelationshipTriggerType;
  objectTypeId: number;
}

export enum AudienceSettingsType {
  self = 0,
  related = 1, // Every person related to the object
  filtered = 2, // Certain people in the account
}
export interface AudienceSettings {
  type: AudienceSettingsType;
  personFilters: SerializedLegacyAttributeFilter[];
  relationshipFilters: SerializedLegacyAttributeFilter[];
}

export enum TriggerCategory {
  'recentlyUsed' = 'recentlyUsed',
  'all' = 'all',
  'people' = 'people',
  'objects' = 'objects',
  'dates' = 'dates',
  'webhooks' = 'webhooks',
}

export const TRIGGER_CATEGORIES_NAMES_FOR_TRACKING: Record<TriggerCategory, string> = {
  [TriggerCategory.recentlyUsed]: 'Recent',
  [TriggerCategory.all]: 'All Triggers',
  [TriggerCategory.people]: 'People',
  [TriggerCategory.objects]: 'Object Type',
  [TriggerCategory.dates]: 'Dates',
  [TriggerCategory.webhooks]: 'Webhooks',
};

export const CAMPAIGN_TYPE_CATEGORIES: Record<CampaignType, CampaignTypeCategory | null> = {
  [CampaignType.Transactional]: CampaignTypeCategory.People,
  [CampaignType.Behavioral]: CampaignTypeCategory.People,
  [CampaignType.APITriggered]: CampaignTypeCategory.People,
  [CampaignType.Date]: CampaignTypeCategory.People,
  [CampaignType.Form]: CampaignTypeCategory.People,
  [CampaignType.Webhook]: CampaignTypeCategory.Data,
  [CampaignType.Object]: CampaignTypeCategory.People,
  [CampaignType.ObjectRelationship]: CampaignTypeCategory.People,
  [CampaignType.None]: null,
};

export const TYPE_ICONS: Record<CampaignType, string> = {
  [CampaignType.Behavioral]: 'trigger-segment',
  [CampaignType.Transactional]: 'trigger-event',
  [CampaignType.APITriggered]: 'api-triggered-broadcast',
  [CampaignType.Date]: 'trigger-date',
  [CampaignType.Form]: 'form',
  [CampaignType.Webhook]: 'webhook',
  [CampaignType.Object]: 'object',
  [CampaignType.ObjectRelationship]: 'connections',
  [CampaignType.None]: 'campaigns',
};

export const TYPE_NAMES: Record<CampaignType, string> = {
  [CampaignType.Behavioral]: 'Segment triggered campaign',
  [CampaignType.Transactional]: 'Event triggered campaign',
  [CampaignType.APITriggered]: 'API triggered broadcast',
  [CampaignType.Date]: 'Date triggered campaign',
  [CampaignType.Form]: 'Form triggered campaign',
  [CampaignType.Webhook]: 'Webhook triggered campaign',
  [CampaignType.Object]: 'Object triggered campaign',
  [CampaignType.ObjectRelationship]: 'Relationship triggered campaign',
  [CampaignType.None]: 'Campaign',
};

export const TYPE_NAMES_FOR_TRACKING: Record<CampaignType, string> = {
  [CampaignType.Behavioral]: 'Segment',
  [CampaignType.Transactional]: 'Event',
  [CampaignType.APITriggered]: 'API',
  [CampaignType.Form]: 'Form',
  [CampaignType.Date]: 'Date',
  [CampaignType.Webhook]: 'Webhook',
  [CampaignType.Object]: 'Object',
  [CampaignType.ObjectRelationship]: 'Relationship',
  [CampaignType.None]: 'None',
};

export const TYPE_NAME_ARTICLES: Record<CampaignType, string> = {
  [CampaignType.Behavioral]: 'a',
  [CampaignType.Transactional]: 'an',
  [CampaignType.APITriggered]: 'an',
  [CampaignType.Date]: 'a',
  [CampaignType.Form]: 'a',
  [CampaignType.Webhook]: 'a',
  [CampaignType.Object]: 'an',
  [CampaignType.ObjectRelationship]: 'a',
  [CampaignType.None]: 'a',
};

export const RESTRICTED_ACTION_GROUPS: Partial<Record<CampaignType, string[]>> = {
  [CampaignType.APITriggered]: ['delays'],
};

export const RESTRICTED_ACTION_TYPES: Partial<Record<CampaignType, ActionType[]>> = {
  [CampaignType.Behavioral]: [],
  [CampaignType.Transactional]: [],
  [CampaignType.Date]: [],
  [CampaignType.Form]: [],
  [CampaignType.APITriggered]: [
    'conditional_wait_action',
    'delay_seconds_action',
    'delay_time_window_action',
    'static_seg_update_action',
    'batch_update_action',
  ],
  [CampaignType.Webhook]: [
    'email_action',
    'in_app_action',
    'push_action',
    'twilio_action',
    'static_seg_update_action',
    'collection_query_action',
    'conditional_wait_action',
  ],
};

export enum CampaignRestartModes {
  None = 'none',
  Rematch = 'rematch',
  Interval = 'interval',
}

export type EdgeType = 'continue' | 'branch';

export type Edge = {
  from: string;
  to: string;
  type: EdgeType;
  index?: number;
};

export type ConsolidatedActionsGroup = {
  parentActionId: string;
  defaultActionId: string;
  branchedActionIds: Set<string>;
  edges: Edge[];
  name: string;
  actionLanguageMap: Record<string, string>;
};

export type ParsedTreeBranchNodeBranch = {
  id: string;
  action: ActionModel;
  type: 'branch';
  edge: Edge;
  edges: {
    edge: Edge;
    nodes: ParsedTreeBranchNode[];
    allEdgeNodes: ActionModel[];
  }[];
};
export type ParsedTreeBranchNodeAction = {
  id: string;
  action: ActionModel;
  type: 'action';
  edge: Edge;
};
export type ParsedTreeBranchNode = ParsedTreeBranchNodeBranch | ParsedTreeBranchNodeAction;

export enum CampaignUpdateTypes {
  Main = 'main',
  State = 'state',
  ExitAndConversion = 'exit_and_conversion',
  Recipients = 'recipients',
  Edges = 'edges',
  AddActions = 'add_actions',
  DeleteActions = 'delete_actions',
  SaveAction = 'save_action',
  SendToUnsubscribed = 'send_to_unsubs',
}

// actions: sent in PUT request with update_type = "state"
export enum CampaignStateActions {
  Archive = 'archive',
  Unarchive = 'unarchive', // "archived"->"stopped" transition
  Stop = 'stop', // "running"/"stopping"->"stopped": flushes all active journeys
  Sunset = 'sunset', // "running"->"sunsetting": allows active journeys to continue. Moves to "stopped" after all active journeys are finished
  Restart = 'restart', // "stopped"->"running"
  Start = 'start', // "draft"->"running"
}

export enum CampaignState {
  Running = 'running',
  Draft = 'draft',
  Stopped = 'stopped',
  Archived = 'archived',
  Sunsetting = 'sunsetting',

  // transition states: backfills in progress
  Stopping = 'stopping', // in transition to "stopped"
  Archiving = 'archiving', // in transition to "archived"
  Unarchiving = 'unarchiving', // in transition to "stopped" (from "archived")
}

export const CAMPAIGN_STATE_LABELS: Record<CampaignState, string> = {
  [CampaignState.Running]: 'Running',
  [CampaignState.Draft]: 'Draft',
  [CampaignState.Stopped]: 'Stopped',
  [CampaignState.Archived]: 'Archived',
  [CampaignState.Sunsetting]: 'Stopping',
  [CampaignState.Stopping]: 'Stopping',
  [CampaignState.Archiving]: 'Archiving',
  [CampaignState.Unarchiving]: 'Unarchiving',
};

export const CAMPAIGN_STATE_CLASSES: Record<CampaignState, string> = {
  [CampaignState.Running]: 'is-success',
  [CampaignState.Draft]: 'is-pending',
  [CampaignState.Stopped]: 'is-danger',
  [CampaignState.Archived]: 'is-pending',
  [CampaignState.Sunsetting]: 'is-danger',
  [CampaignState.Stopping]: 'is-danger',
  [CampaignState.Archiving]: 'is-pending',
  [CampaignState.Unarchiving]: 'is-pending',
};

export interface APIJourneyMetric {
  campaign_id: string;
  data: number[];
  id: string;
  metric: 'activated' | 'converted' | 'exited_early' | 'finished' | 'messaged' | 'never_activated' | 'started';
  resolution: 'daily' | 'weekly';
  start_time: number;
}

export const PARSE_TREE_RETRY_THRESHOLD_IN_MILLISECONDS = 1000;

export const RECENT_LOG_REQUEST_THRESHOLD_IN_SECONDS = 2;

export default class CampaignModel extends Model implements ModelWithConversion {
  @service account!: AccountService;
  @service api!: ApiService;
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;

  @attr('string') name!: string;
  @attr('string') description!: string;
  @attr('string') type!: CampaignType;

  get typeCategory() {
    return CAMPAIGN_TYPE_CATEGORIES[this.type];
  }

  @equal('typeCategory', CampaignTypeCategory.People) isPeopleCampaign!: boolean;
  @equal('typeCategory', CampaignTypeCategory.Data) isDataCampaign!: boolean;

  // This isn't sent to the backend - it's an internal attribute to track
  // campaign models that are only temporarily created for copying purposes in
  // the VWB
  @attr('boolean') _copy!: boolean;

  // "state" represents campaign states that can be returned in back-end responses
  @attr('string', { defaultValue: CampaignState.Draft }) state!: CampaignState;
  // "stateAction" is serialized to "state" in *outgoing* requests with "updateType"="state", used for state transitions
  // as separate property to avoid broken UI when "campaign.state" is not one of real campaign states, safer type-checking
  @attr('string') stateAction!: CampaignStateActions;

  @attr('number') attempted!: number;
  @attr('number') sent!: number;
  @attr('number') delivered!: number;
  @attr('number') created!: number;
  @attr('number') firstStarted!: number;
  @attr('number') updated!: number;
  @attr('number') sentAt!: number;

  @attr('boolean', { defaultValue: false }) sample!: boolean;
  @attr('boolean', { allowNull: true }) backfill!: boolean;
  @attr('boolean') deleted!: boolean;
  @attr('boolean') sendToUnsubscribed!: boolean;
  @attr('boolean') useMessageLimits!: boolean;
  @attr('boolean') useWorkspaceMessageLimitsRetry!: boolean;
  @attr('boolean') unsentDrafts!: boolean;
  @attr('number') messageLimitsRetryWindowSecs!: number;

  @attr('number') queuedTriggers!: number;
  @attr('number') triggerCount!: number;

  @attr('string') updateType!: CampaignUpdateTypes;

  @attr('base64-json') anchors!: SerializedLegacySegmentNegatableFilter[];
  @attr('base64-json') filters!: SerializedLegacySegmentNegatableFilter[];

  @attr() attributeFilters!: SerializedLegacyAttributeFilters;
  @attr() channelMetrics!: Record<Channel, any>;

  @attr('recipients-json') recipientsFilter!: unknown[];

  @attr('string') event!: string;
  @attr('number', { defaultValue: 0 }) restartMinInterval!: number;

  @equal('restartMinInterval', 0) enterEveryTimeEventIsSent!: boolean;
  @equal('restartMinInterval', -1) enterFirstTimeEventIsSent!: boolean;
  @gt('restartMinInterval', 0) enterOnceWithinPeriodWhenEventIsSent!: boolean;

  @attr('boolean', { defaultValue: true }) exitOnTriggerOrFilterNotMatched!: boolean;
  @attr('boolean') exitOnConversionMatched!: boolean;
  @attr('boolean', { defaultValue: false }) exitOnlyAfterMessage!: boolean;

  @computed('type')
  get canConvert() {
    return !this.isTypeNone && !this.isWebhookTriggered;
  }

  @attr('boolean', { allowNull: true }) conversion!: boolean | null;
  @attr('string') conversionType!: ConversionType | '';
  @computed('conversionType')
  get isEventConversion(): boolean {
    return isConversionEventBased(this);
  }
  @computed('conversionType')
  get isSegmentConversion(): boolean {
    return isConversionSegmentBased(this);
  }
  @attr('string') conversionAction!: string;
  @attr('string') conversionSegmentId!: string;
  @attr('number') conversionWindow!: number;
  @attr('string') conversionEventName!: string;
  @attr() conversionEventFilters!: Filters | null;

  @attr('string') dateTriggeredFrequency!: FrequencyOption;
  @attr('number') dateTriggeredLeadDays!: number;
  @attr('string') dateTriggeredStartTime!: string; // e.g. "9:30 AM"
  @attr('string') dateTriggeredAttribute!: string;
  @attr('string') dateTriggeredZone!: string;
  @attr('string') dateTriggeredFallbackZone!: string;

  @attr('string') webhookId!: string | null;

  @attr('string') objectTypeId!: number | null;
  @attr('base64-json') objectAttributeTriggers!: SerializedLegacyAttributeFilter[] | null; // Used as trigger conditions that pull in the Objects in the Campaign
  @attr('base64-json') objectFilters!: SerializedLegacyAttributeFilter[] | null; // Used as Object filters before any non-delay action
  @attr('base64-json') relationshipAttributeTriggers!: SerializedLegacyAttributeFilter[] | null; // Used as trigger conditions that pull in the Relationships in the Campaign
  @attr('base64-json') relationshipFilters!: SerializedLegacyAttributeFilter[] | null; // Used as Relationship filters before any non-delay action
  @attr('base64-json') triggerPersonFilters!: SerializedLegacySegmentNegatableFilter[] | null; // Used as a trigger person filters before any non-delay action
  @attr('base64-json') triggerRelationshipFilters!: SerializedLegacyAttributeFilter[] | null; // Used as a trigger relationship filters before any non-delay action

  @attr('camel-case') objectCampaign!: ObjectCampaignTrigger | null;
  @attr('camel-case') relationshipCampaign!: RelationshipCampaignTrigger | null;

  @attr('base64-json', { nestedKeys: ['person_filters', 'relationship_filters'], camelCase: true })
  audience!: AudienceSettings | null;

  get recipientNoun() {
    if (this.isDataCampaign) {
      return {
        singular: 'object',
        plural: 'objects',
      };
    }

    return {
      singular: 'person',
      plural: 'people',
    };
  }

  // Used in campaign setup to populate dropdowns with available keys
  @tracked latestWebhookLog: LogModel | null = null;

  async fetchLatestWebhookLog(clearLog = false) {
    let task = this.fetchLatestWebhookLogTask;
    if (task.isRunning && task.last != null) {
      return task.last;
    }
    return task.perform(clearLog);
  }

  fetchLatestWebhookLogTask = task({ drop: true }, async (clearLog = false) => {
    if (clearLog) {
      this.latestWebhookLog = null;
    }

    if (this.latestWebhookLog != null) {
      return this.latestWebhookLog;
    }

    let logs = await this.fetchWebhookEventLogs();

    if (logs.length > 0) {
      let [firstLog] = logs;
      this.latestWebhookLog = firstLog;

      return firstLog;
    }

    return null;
  });

  latestLogs: LogModel[] = [];
  latestLogQueryTimestamp = 0;

  async fetchWebhookEventLogs() {
    let task = this.fetchWebhookEventLogsTask;
    if (task.isRunning && task.last != null) {
      return task.last;
    }
    return task.perform();
  }

  fetchWebhookEventLogsTask = task({ drop: true }, async () => {
    // If a request for logs was performed in the past X seconds, seconds,
    // return the previous results
    let now = unixNowSeconds();
    let shouldUseExistingLogs =
      this.latestLogQueryTimestamp > now - RECENT_LOG_REQUEST_THRESHOLD_IN_SECONDS && !Ember.testing;
    if (shouldUseExistingLogs) {
      return this.latestLogs;
    }

    this.latestLogQueryTimestamp = now;

    let logs = _sortBy(
      (
        await this.store.query('log', {
          type: ActivityLogType.WebhookEvent,
          name: this.id,
        })
      ).toArray(),
      ['timestamp'],
    ).reverse();
    this.latestLogs = logs;

    return logs;
  });

  @computed('webhookId', 'account.current.dataCenter')
  get webhookUrl() {
    return webhookUrl(this.webhookId, this.account.current);
  }

  @attr('string', { defaultValue: CampaignRestartModes.None }) restartMode!: string;
  @equal('restartMode', CampaignRestartModes.None) neverRestart!: boolean;
  @equal('restartMode', CampaignRestartModes.Rematch) restartOnRematch!: boolean;
  @equal('restartMode', CampaignRestartModes.Interval) restartOnFixedInterval!: boolean;

  @equal('state', CampaignState.Draft) isDraft!: boolean;
  @equal('state', CampaignState.Running) isRunning!: boolean;
  @equal('state', CampaignState.Sunsetting) isSunsetting!: boolean;
  @equal('state', CampaignState.Stopping) isStopping!: boolean;
  @equal('state', CampaignState.Stopped) isStopped!: boolean;
  @equal('state', CampaignState.Archiving) isArchiving!: boolean;
  @equal('state', CampaignState.Archived) isArchived!: boolean;
  @equal('state', CampaignState.Unarchiving) isUnarchiving!: boolean;
  @computed('state')
  get isStateChangeBackfilling(): boolean {
    return (
      this.state === CampaignState.Archiving ||
      this.state === CampaignState.Stopping ||
      this.state === CampaignState.Unarchiving
    );
  }
  @or('isArchived', 'isStateChangeBackfilling') isLocked!: boolean;

  @equal('type', CampaignType.None) isTypeNone!: boolean;
  @equal('type', CampaignType.Behavioral) isSegmentTriggered!: boolean;
  @equal('type', CampaignType.Transactional) isEventTriggered!: boolean;
  @equal('type', CampaignType.Form) isFormTriggered!: boolean;
  @equal('type', CampaignType.APITriggered) isApiTriggered!: boolean;
  @equal('type', CampaignType.Date) isDateTriggered!: boolean;
  @equal('type', CampaignType.Webhook) isWebhookTriggered!: boolean;
  @equal('type', CampaignType.Object) isObjectTriggered!: boolean;
  @equal('type', CampaignType.ObjectRelationship) isObjectRelationshipTriggered!: boolean;

  @computed('neverRestart', 'isSegmentTriggered')
  get isRestartable() {
    return !this.neverRestart && this.isSegmentTriggered;
  }

  @belongsTo('user-other-account', { async: false, inverse: 'createdCampaigns' }) createdByUser!: UserOtherAccountModel;
  @belongsTo('user-other-account', { async: false, inverse: 'updatedCampaigns' }) updatedByUser!: UserOtherAccountModel;
  @attr('string') subscriptionTopicId?: string;

  @attr() workflowActionIds!: string[];
  @hasMany('action', { async: false, inverse: 'campaign' }) actions!: ArrayProxy<ActionModel>;
  @hasMany('broadcast-trigger', { async: true, inverse: 'campaign' })
  broadcastTriggers!: DS.PromiseManyArray<BroadcastTriggerModel>;

  @belongsTo('broadcast-trigger', { async: true, inverse: null }) mostRecentTrigger!: BroadcastTriggerModel;
  @hasMany('tag', { async: true, inverse: 'campaigns' }) tags!: DS.PromiseManyArray<TagModel>;
  @hasMany('campaign-note', { async: false, inverse: 'campaign' }) notes!: CampaignNoteModel[];

  tagSorting = ['name'];
  @sort('tags.content', 'tagSorting') sortedTags!: TagModel[];

  @attr({
    defaultValue() {
      return [];
    },
  })
  edges!: Edge[];

  get subscriptionTopic() {
    if (this.subscriptionTopicId == null || !this.subscriptionCenter.initialized) {
      return null;
    }

    return this.subscriptionCenter.topics.find((topic) => topic.id === this.subscriptionTopicId);
  }

  get hasDeletedSubscriptionTopic() {
    return !isEmpty(this.subscriptionTopicId) && this.subscriptionTopic == null;
  }

  @computed('allNotDeletedActions.[]')
  get hasFilterMatchDelayAction(): boolean {
    return this.allNotDeletedActions.some((action: ActionModel) => action.type === 'filter_match_delay_action');
  }

  @computed('allNotDeletedActions.[]')
  get hasMultiLanguageBranchAction(): boolean {
    return this.allNotDeletedActions.some((action: ActionModel) => action.type === 'multi_lang_branch_action');
  }

  @computed('workflowActionIds', 'actions.[]')
  get allNotDeletedActions(): ActionModel[] {
    let { workflowActionIds } = this;
    let actions = this.actions.toArray().rejectBy('actionState', ActionState.Deleting);
    return (workflowActionIds ?? []).map((id) => actions.findBy('id', `${id}`)).compact() as ActionModel[];
  }

  @computed('workflowActionIds', 'actions.[]')
  get workflowActions(): ActionModel[] {
    let { workflowActionIds } = this;
    let actions = this.actions.toArray().rejectBy('type', 'filter_match_delay_action');
    return (workflowActionIds ?? []).map((id) => actions.findBy('id', `${id}`)).compact() as ActionModel[];
  }

  @computed('allNotDeletedActions.[]')
  get exitActionIds() {
    return this.allNotDeletedActions.filter((a) => a.isExit).map((a) => a.id);
  }

  @computed('workflowActions.@each.type')
  get messagingActions(): ActionModel[] {
    return this.workflowActions.filter((action: ActionModel) => action.isMessagingType || action.isSplitTest);
  }

  @filterBy('workflowActions', 'isEffect') effectActions!: ActionModel[];
  @filterBy('workflowActions', 'hasEmailStats') emailStatActions!: ActionModel[];
  @filterBy('workflowActions', 'isSplitTest') splitTestActions!: ActionModel[];

  @computed('sendToUnsubscribed', 'workflowActions.@each.sendToUnsubscribed')
  get sendToUnsubscribedActions(): ActionModel[] {
    return this.workflowActions.filter(
      (action: ActionModel) =>
        (action.isMessagingType || action.isSplitTest) &&
        (action.sendToUnsubscribed || (this.sendToUnsubscribed && action.sendToUnsubscribed === null)),
    );
  }

  @computed('workflowActions.@each.subscriptionTopicId')
  get actionsOverridingCampaignSettings() {
    type TopicsKey = 'sendToUnsubscribed' | 'sendToSubscribed' | number;
    let groupedResults: { [key in TopicsKey]?: ActionModel[] } = {};

    this.messagingActions
      .filter((action) => this.isOverridingCampaignSettings(action))
      .forEach((action: ActionModel) => {
        let { subscriptionTopicId, sendToUnsubscribed } = action;
        if (subscriptionTopicId != null) {
          if (!isNone(groupedResults[subscriptionTopicId])) {
            groupedResults[subscriptionTopicId]?.push(action);
          }
          groupedResults[subscriptionTopicId] = [action];
          return;
        }
        if (sendToUnsubscribed === true) {
          groupedResults.sendToUnsubscribed = [...(groupedResults.sendToUnsubscribed ?? []), action];
        }
        if (sendToUnsubscribed === false) {
          groupedResults.sendToSubscribed = [...(groupedResults.sendToSubscribed ?? []), action];
        }
      });
    return groupedResults;
  }

  @computed('sendToUnsubscribedActions.[]')
  get sendToUnsubscribedCount() {
    return this.sendToUnsubscribedActions.length;
  }

  @computed('messagingActions.@each.subscriptionTopicId')
  get hasActionsUsingDeletedSubscriptionTopic() {
    if (!this.subscriptionCenter.initialized) {
      return false;
    }
    return this.actionsUsingSubscriptionTopic.some((topicId) => {
      return this.subscriptionCenter.topics.find((topic) => topic.id === topicId.toString()) == null;
    });
  }

  @computed('messagingActions.@each.{subscriptionTopicId,sendToUnsubscribed}')
  get actionsUsingSubscriptionTopic() {
    return this.messagingActions
      .filter((action: ActionModel) => action.subscriptionTopicId != null)
      .map((action) => action.subscriptionTopicId);
  }

  @computed('useMessageLimits', 'workflowActions.@each.useMessageLimits')
  get useMessageLimitsActions(): ActionModel[] {
    return this.workflowActions.filter(
      (action) =>
        (action.isMessagingType || action.isSplitTest) &&
        (action.useMessageLimits || (this.useMessageLimits && action.useMessageLimits === null)),
    );
  }

  @computed('useMessageLimitsActions.[]')
  get useMessageLimitsCount() {
    return this.useMessageLimitsActions.length;
  }

  @computed('actions.[]')
  get actionIndexIncrement() {
    let hiddenAction = this.actions.find((item) => item.type === 'filter_match_delay_action');
    return hiddenAction ? 1 : 0;
  }

  @notEmpty('messagingActions') hasMessages!: boolean;
  @notEmpty('effectActions') hasEffectActions!: boolean;
  @equal('conversion', true) hasConversion!: boolean;
  @notEmpty('variation') hasVariation!: boolean;
  @notEmpty('filters') hasFilters!: boolean;

  get hasObjectFilters() {
    return !isEmpty(this.objectFilters);
  }
  get hasRelationshipFilters() {
    return !isEmpty(this.relationshipFilters);
  }
  get hasAnyFilters() {
    return this.hasFilters || this.hasObjectFilters || this.hasRelationshipFilters;
  }

  validConversionTimeframe = true;

  @computed(
    'type',
    'canConvert',
    'conversion',
    'conversionType',
    'conversionSegmentId',
    'conversionEventName',
    'exitOnConversionMatched',
    'validConversionTimeframe',
  )
  get validConversion() {
    if (this.type === CampaignType.None) {
      return false;
    }

    if (!this.canConvert) {
      return true;
    }

    return (
      isValidConversion(
        this.conversion,
        this.conversionType,
        this.conversionSegmentId,
        this.conversionEventName,
        this.validConversionTimeframe,
      ) && !(this.exitOnConversionMatched && !this.conversion)
    );
  }

  get validWorkflowSettings() {
    if (this.hasDeletedSubscriptionTopic) {
      return false;
    }

    if (!this.subscriptionCenter.enabled) {
      return true;
    }

    return this.type === CampaignType.Webhook || this.subscriptionTopic != null || this.sendToUnsubscribed;
  }

  get validActionsSettings() {
    if (!this.subscriptionCenter.enabled) {
      return true;
    }

    // Send to All subscribed people is not a valid setting when the subscription center is enabled
    return (this.actionsOverridingCampaignSettings.sendToSubscribed?.length ?? 0) === 0;
  }

  @computed('type')
  get typeName() {
    return {
      [CampaignType.Behavioral]: 'Segment triggered campaign',
      [CampaignType.Transactional]: 'Event triggered campaign',
      [CampaignType.Form]: 'Form triggered campaign',
      [CampaignType.Date]: 'Date triggered campaign',
      [CampaignType.APITriggered]: 'API triggered broadcast',
      [CampaignType.Webhook]: 'Webhook triggered campaign',
      [CampaignType.Object]: 'Object triggered campaign',
      [CampaignType.ObjectRelationship]: 'Relationship triggered campaign',
      [CampaignType.None]: 'Campaign',
    }[this.type];
  }

  @computed('recipientsFilter')
  get recipientsFilterQuery() {
    return queryEncode(this.recipientsFilter);
  }

  @and('areRecipientsReady', 'hasEffectActions', 'validConversion', 'validWorkflowSettings', 'validActionsSettings')
  allReady!: boolean;
  @or('hasRecipients', 'isApiTriggered') areRecipientsReady!: boolean;

  @computed(
    'anchors',
    'event',
    'recipientsFilter',
    'type',
    'dateTriggeredFrequency',
    'dateTriggeredAttribute',
    'dateTriggeredStartTime',
    'dateTriggeredZone',
    'webhookId',
    'attributeFilters',
    'objectAttributeTriggers',
    'relationshipAttributeTriggers',
  )
  get hasRecipients(): boolean {
    if (this.isSegmentTriggered) {
      return !isEmpty(this.anchors);
    } else if (this.isApiTriggered) {
      return !isEmpty(this.recipientsFilter);
    } else if (this.isEventTriggered) {
      return !isEmpty(this.event);
    } else if (this.isFormTriggered) {
      let formConditions = deserializeAndNormalizeFormAttributeFilters(this.attributeFilters);
      let formIDs = formConditions.children.map((c: FormTriggeredCampaignFormTriggerFilter) => c.children[0].value);

      // - must have at least 1 form trigger
      // - all form triggers must have a selected form
      return this.event === 'form_submit' && formIDs.length > 0 && formIDs.every((id) => id);
    } else if (this.isDateTriggered) {
      return (
        !isEmpty(this.dateTriggeredFrequency) &&
        !isEmpty(this.dateTriggeredAttribute) &&
        !isEmpty(this.dateTriggeredStartTime) &&
        !isEmpty(this.dateTriggeredZone)
      );
    } else if (this.isWebhookTriggered) {
      return this.webhookId != null;
    } else if (this.isObjectTriggered) {
      let hasTriggers = true;
      if (this.objectCampaign?.type === ObjectTriggerType.Updated) {
        hasTriggers = !isEmpty(this.objectAttributeTriggers);
      }
      return this.objectCampaign?.type != null && this.objectCampaign?.objectTypeId != null && hasTriggers;
    } else if (this.isObjectRelationshipTriggered) {
      let hasTriggers = true;
      if (this.relationshipCampaign?.type === ObjectRelationshipTriggerType.Updated) {
        hasTriggers = !isEmpty(this.relationshipAttributeTriggers);
      }
      return this.relationshipCampaign?.type != null && this.relationshipCampaign?.objectTypeId != null && hasTriggers;
    }

    return false;
  }

  get triggerFormTypes() {
    let formConditions = deserializeAndNormalizeFormAttributeFilters(this.attributeFilters);
    let formTypes = formConditions.children.map((c: FormTriggeredCampaignFormTriggerFilter) => c.children[0].formType);

    return [...new Set(formTypes)];
  }

  @computed('type')
  get icon(): string {
    return TYPE_ICONS[this.type];
  }

  @computed('type')
  get shortTypeName(): string {
    return shortTypeName(this);
  }

  @computed('updated', 'created')
  get wasModified() {
    return this.updated !== this.created;
  }

  get objectTypeForCampaign() {
    if (this.relationshipCampaign != null || this.objectCampaign != null) {
      let objectTypeId = this.relationshipCampaign?.objectTypeId ?? this.objectCampaign?.objectTypeId;

      if (objectTypeId != null) {
        return this.store.peekRecord('object-type', objectTypeId);
      }
    }

    return null;
  }

  @equal('triggerCount', 0) neverTriggered!: boolean;

  saveWithoutType() {
    this.set('type', CampaignType.None);
    return this.save();
  }

  lastParseTreeTimestamp = 0;
  lastParseTreeUpdatedAt = 0;
  branchTree: ParsedTreeBranchNode[] = [];
  branchesMap: Record<string, ParsedTreeBranchNodeBranch> = {};
  branchRejoinPointsMap: Record<string, string> = {};
  parentBranchMap: Record<string, string> = {};
  traversedActions: ParsedTreeBranchNode[] = [];
  // actionDepths tells us what depth a given action id is at, when nested
  // within branch actions
  actionDepths: Record<string, number> = {};
  // For nested actions, this tells us what all the branch actions up the chain
  // are, all the way to the root
  parentBranchChains: Record<string, string[]> = {};
  // Keeps an order of actions as traversed from top to bottom, left to right
  traversalIndices: Record<string, number> | null = null;
  // Edges after consolidation
  consolidatedEdges: Edge[] = [];
  // Resulting actions after edges were consolidated
  consolidatedActions: ActionModel[] = [];
  // Groups of consolidated actions
  consolidatedActionsGroups: Record<string, ConsolidatedActionsGroup> = {};
  // Default (visible) action of consolidated branches
  consolidatedBranchDefaultActions = new Set<string>();
  // Branched actions and their corresponding parent branch action
  parentConsolidatedBranchMap: Record<string, string> = {};

  parseTree(force = false) {
    let {
      branchTree,
      branches,
      branchRejoinPointsMap,
      parentBranchMap,
      traversedActions,
      depths,
      parentBranchChains,
      consolidatedEdges,
      consolidatedActions,
      consolidatedActionsGroups,
      consolidatedBranchDefaultActions,
      parentConsolidatedBranchMap,
    } = parseTree(this.edges, this.allNotDeletedActions);

    let now = unixNowMilliseconds();

    // If the parseTree method has been called recently, don't re-parse the tree
    // again. There can be places in the app where multiple components try and
    // call this method around the same time, or one after the other, and that's
    // not necessary.
    // We also compare the updated timestamp for further security (maybe the
    // campaign got updated between component renders and it actually needs to
    // be re-parsed)
    if (
      this.lastParseTreeTimestamp > 0 &&
      now - this.lastParseTreeTimestamp < PARSE_TREE_RETRY_THRESHOLD_IN_MILLISECONDS &&
      this.lastParseTreeUpdatedAt === this.updated &&
      !Ember.testing &&
      !force
    ) {
      return;
    }

    this.lastParseTreeTimestamp = now;
    this.lastParseTreeUpdatedAt = this.updated;

    this.setProperties({
      branchTree,
      branchesMap: branches,
      branchRejoinPointsMap,
      parentBranchMap,
      traversedActions,
      actionDepths: depths,
      parentBranchChains,
      consolidatedEdges,
      consolidatedActions,
      consolidatedActionsGroups,
      consolidatedBranchDefaultActions,
      parentConsolidatedBranchMap,
    });
  }

  ensureParsedTree() {
    if (this.lastParseTreeTimestamp === 0) {
      this.parseTree();
    }
  }

  @computed('consolidatedEdges', 'consolidatedActions')
  get consolidatedTree() {
    this.ensureParsedTree();

    return {
      edges: this.consolidatedEdges,
      actions: this.consolidatedActions,
    };
  }

  getConsolidatedActionParentBranchActionId(actionId: string | null | undefined): string | null {
    if (actionId == null) {
      return null;
    }

    this.ensureParsedTree();

    if (isEmpty(this.parentConsolidatedBranchMap)) {
      return null;
    }

    return this.parentConsolidatedBranchMap[actionId] ?? null;
  }

  getConsolidatedActionParentBranch(actionId: string | null | undefined): ActionModel | null {
    let parentBranchActionId = this.getConsolidatedActionParentBranchActionId(actionId);

    if (parentBranchActionId != null) {
      return this.store.peekRecord('action', parentBranchActionId);
    }

    return null;
  }

  getConsolidatedBranchChildActions(branchActionId: string | null | undefined): ActionModel[] {
    let childActions: ActionModel[] = [];

    if (branchActionId == null) {
      return childActions;
    }

    this.ensureParsedTree();

    let branchedActionIds = this.consolidatedActionsGroups[branchActionId]?.branchedActionIds ?? [];
    for (let branchedActionId of branchedActionIds) {
      if (branchedActionId != null) {
        childActions.push(this.store.peekRecord('action', branchedActionId) as ActionModel);
      }
    }

    return childActions;
  }

  getConsolidatedBranchChildActionLanguage(childActionId: string) {
    this.ensureParsedTree();

    let parentBranchActionId = this.getConsolidatedActionParentBranchActionId(childActionId);
    if (parentBranchActionId == null) {
      return null;
    }

    let consolidatedGroup = this.consolidatedActionsGroups[parentBranchActionId];

    if (consolidatedGroup?.actionLanguageMap != null) {
      return consolidatedGroup.actionLanguageMap[childActionId];
    }

    return null;
  }

  getLanguageCountByMessagingAction(childActionId: string): number {
    let parentBranchActionId = this.getConsolidatedActionParentBranchActionId(childActionId);
    if (parentBranchActionId == null) {
      return 0;
    }

    let consolidatedGroup = this.consolidatedActionsGroups[parentBranchActionId];
    if (consolidatedGroup?.branchedActionIds != null) {
      return consolidatedGroup.branchedActionIds.size - 1;
    }

    return 0;
  }

  getConsolidatedActionsGroupDefaultActionByBranchActionId(
    branchActionId: string | null | undefined,
  ): ActionModel | null {
    if (branchActionId == null) {
      return null;
    }

    let consolidatedGroup = this.consolidatedActionsGroups[branchActionId];
    if (consolidatedGroup == null) {
      return null;
    }

    return this.store.peekRecord('action', consolidatedGroup.defaultActionId);
  }

  getConsolidatedActionsGroupDefaultActionByChildActionId(
    childActionId: string | null | undefined,
  ): ActionModel | null {
    if (childActionId == null) {
      return null;
    }

    this.ensureParsedTree();

    let parentActionId = this.getConsolidatedActionParentBranchActionId(childActionId);
    if (parentActionId == null) {
      return null;
    }

    let consolidatedGroup = this.consolidatedActionsGroups[parentActionId];
    if (consolidatedGroup == null) {
      return null;
    }

    return this.store.peekRecord('action', consolidatedGroup.defaultActionId);
  }

  getNewEdgesAfterAddingChildActionsToConsolidatedBranch(
    branchActionId: string | null | undefined,
    existingChildActionId: string,
    newChildActionsCount: number,
  ): Edge[] {
    let edges = this.expandConsolidatedEdges(this.edges);

    let creatingNewBranch = branchActionId == null;
    if (creatingNewBranch) {
      branchActionId = '-1';
    }

    if (creatingNewBranch) {
      // Move an existing action to a new branch action.
      // Existing incoming edges now point to the new branch action.
      edges = edges.map((edge) => {
        if (edge.to !== existingChildActionId) {
          return edge;
        }

        return {
          ...edge,
          to: branchActionId,
        };
      });

      // Add a branch edge to connect a new branch action to the existing action.
      let newBranchEdge = {
        from: branchActionId,
        to: existingChildActionId,
        type: 'branch',
        index: newChildActionsCount,
      } as Edge;
      edges.push(newBranchEdge);
    } else {
      // Shift branch edges 'index' properties as they are in reverse order to the branch multiConditions array.
      edges = edges.map((edge) => {
        if (edge.from !== branchActionId || edge.type !== 'branch') {
          return edge;
        }

        return {
          ...edge,
          index: edge.index + newChildActionsCount,
        };
      });
    }

    // We will need this to find out a next action in the workflow to point new "continue" edge(s) to.
    let outgoingEdge = edges.find((edge) => edge.from === existingChildActionId);
    if (outgoingEdge == null) {
      throw new Error('Outgoing edge not found');
    }

    // Add new edges for the new actions. New actions have negative IDs until they are saved on backend.
    let newActionId = creatingNewBranch ? -2 : -1;
    for (let branchEdgeIndex = 0; branchEdgeIndex < newChildActionsCount; branchEdgeIndex++) {
      edges.push({
        from: branchActionId,
        to: `${newActionId}`,
        type: 'branch',
        index: branchEdgeIndex,
      } as Edge);

      edges.push({
        from: `${newActionId}`,
        to: outgoingEdge.to,
        type: 'continue',
      } as Edge);

      newActionId--;
    }

    return edges;
  }

  findTraversalIndices() {
    let quickGraph = quickGraphRank(this);

    let indices: Record<string, number> = {};

    let index = 0;
    quickGraph.traversalOrder.forEach((id) => {
      // "virtual" nodes, like path label nodes or drop nodes, can be ignored
      // for the purposes of this ordering. They all contain an underscore in
      // their id, so they're easy to filter out
      if (id.includes('_')) {
        return;
      }

      indices[id] = index;
      index++;
    });

    this.set('traversalIndices', indices);
  }

  unloadActions(actionIds: string[]) {
    actionIds.forEach((id) => {
      this.store.peekRecord('action', id)?.unloadRecord();
    });
  }

  removeActionFromWorkflow(action: ActionModel, { waitUntilDeleteOption = '' } = {}) {
    let rejoinPoint = action.isBranch ? this.branchRejoinPointsMap[action.id] : null;
    let { edges } = removeNodeFromGraph(action, this.consolidatedTree.edges, [], rejoinPoint);
    let actionIds = getActionIdsInConsolidatedGroup(
      action.id,
      this.consolidatedActionsGroups,
      this.parentConsolidatedBranchMap,
    );

    return this.deleteActions({
      actionIds,
      edges,
      touchedActionIds: [],
      waitUntilDeleteOption,
    }).finally(() => {
      this.unloadActions(actionIds);
      this.parseTree();
    });
  }

  expandConsolidatedEdges(newEdges: Edge[]) {
    let { consolidatedActionsGroups, parentConsolidatedBranchMap } = this as CampaignModel;

    return expandTree(consolidatedActionsGroups, parentConsolidatedBranchMap, newEdges);
  }

  private isOverridingCampaignSettings(action: ActionModel) {
    // webhook actions do not override campaign settings
    if (action.type === 'webhook_action') {
      return false;
    }
    // case 1: action uses subscriptionTopicId different from campaign
    if (!isNone(action.subscriptionTopicId)) {
      return action.subscriptionTopicId.toString() !== this.subscriptionTopicId;
    }
    // case 2: action uses sendToUnsubscribed value different from action
    if (action.sendToUnsubscribed !== null) {
      return action.sendToUnsubscribed !== this.sendToUnsubscribed || !isNone(this.subscriptionTopicId);
    }
    if (action.sendToUnsubscribed === null) {
      return this.sendToUnsubscribed !== null || !isNone(this.subscriptionTopicId);
    }
    return false;
  }

  addActions = modelApiAction({
    type: 'PUT',
    path: '',
    before({ actions, edges, copyFromEnv, defaultLanguageActionId }) {
      let serializedCampaign: Record<string, unknown> = this.serialize({ includeId: true }) as Record<string, unknown>;
      serializedCampaign.update_type = CampaignUpdateTypes.AddActions;

      let data: Record<string, unknown> = {
        actions,
        campaign: serializedCampaign,
      };

      if (copyFromEnv) {
        data.copy_from_env = copyFromEnv;
        serializedCampaign.edges = edges;
      } else {
        serializedCampaign.edges = (this as CampaignModel).expandConsolidatedEdges(edges);
      }

      if (!isEmpty(defaultLanguageActionId)) {
        data.default_language_action_id = defaultLanguageActionId;
      }

      return data;
    },
    after(this: CampaignModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  moveAction = modelApiAction({
    type: 'PUT',
    path: '',
    before({ edges, touchedActionIds }) {
      let serializedCampaign: Record<string, unknown> = this.serialize({ includeId: true }) as Record<string, unknown>;
      serializedCampaign.edges = (this as CampaignModel).expandConsolidatedEdges(edges);
      serializedCampaign.update_type = CampaignUpdateTypes.Edges;

      return {
        campaign: serializedCampaign,
        touched_action_ids: [...new Set(touchedActionIds)],
      };
    },
    after(this: CampaignModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  deleteActions = modelApiAction({
    type: 'PUT',
    path: '',
    before(
      this: CampaignModel,
      { actionIds, edges, touchedActionIds, waitUntilDeleteOption, skipExpandConsolidatedEdges = false },
    ) {
      let serializedCampaign: Record<string, unknown> = this.serialize({ includeId: true }) as Record<string, unknown>;
      serializedCampaign.edges = skipExpandConsolidatedEdges ? edges : this.expandConsolidatedEdges(edges);
      serializedCampaign.update_type = CampaignUpdateTypes.DeleteActions;
      serializedCampaign.workflow_action_ids = this.workflowActionIds.filter((id) => !actionIds.includes(id));

      let payload: Record<string, unknown> = {
        action_ids: actionIds,
        campaign: serializedCampaign,
        touched_action_ids: [...new Set(touchedActionIds)],
      };

      if (waitUntilDeleteOption === 'exitEarly') {
        payload.exit_early = true;
      }

      return payload;
    },
    after(this: CampaignModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  saveAction = modelApiAction({
    type: 'PUT',
    path: '',
    before({ action, edges }) {
      let serializedCampaign: Record<string, unknown> = this.serialize({ includeId: true }) as Record<string, unknown>;
      serializedCampaign.edges = (this as CampaignModel).expandConsolidatedEdges(edges);
      serializedCampaign.update_type = CampaignUpdateTypes.SaveAction;

      return {
        action: action.serialize({ includeId: true }),
        campaign: serializedCampaign,
        touched_action_ids: [action.id],
      };
    },
    after(this: CampaignModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  getJourneyMetrics = modelApiAction({
    path: 'journey_metrics',
    type: 'GET',
    before({ resolution, startTime, endTime }) {
      return {
        resolution,
        start_time: startTime,
        end_time: endTime,
      };
    },
    after(response): APIJourneyMetric[] {
      return response.journey_metric;
    },
  });

  getTotalJourneyMetrics = modelApiAction({
    path: 'total_journey_metrics',
    type: 'GET',
    after(response) {
      let metric = response.total_journey_metrics;
      let camelizedMetric: { [key: string]: number } = {};

      Object.keys(metric).forEach((key) => {
        camelizedMetric[camelize(key)] = metric[key];
      });

      return camelizedMetric;
    },
  });

  addTags = modelApiAction({
    path: 'tags',
    type: 'POST',
    before(tags: TagModel[]) {
      return {
        tags: tags.map((tag) => ({ id: Number(tag.id), name: tag.name })),
      };
    },

    after(this: CampaignModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  createWebhookEndpoint = modelApiAction({
    path: 'webhooks',
    type: 'POST',
    after(response) {
      return response.webhook_id;
    },
  });

  async removeTag(tag: TagModel) {
    await this.api.delete(`campaigns/${this.id}/tags/${tag.id}`, {});

    this.set(
      'tags',
      (this.tags.content as TagModel[]).filter((t) => t !== tag),
    );
  }

  changeState(
    this: CampaignModel,
    opts:
      | {
          stateAction: Exclude<CampaignStateActions, CampaignStateActions.Restart | CampaignStateActions.Start>;
        }
      | {
          stateAction: CampaignStateActions.Restart | CampaignStateActions.Start;
          backfill?: boolean;
        },
  ) {
    this.setProperties({
      updateType: CampaignUpdateTypes.State,
      stateAction: opts.stateAction,
    });

    if (opts.stateAction === CampaignStateActions.Restart || opts.stateAction === CampaignStateActions.Start) {
      this.set('backfill', opts.backfill);
    }

    return this.save();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    campaign: CampaignModel;
  }
}
